/**
 * Admin Reseller VIEWS RPC definitions.
 *
 * Name-resolved read models for the reseller RATES / CUSTOMERS / EARNINGS admin
 * screens, plus the two attribution writes (attach / detach). Distinct from the
 * fully-qualified `AdminResellersRpcs` group (which the `claw` CLI consumes):
 * these methods return the target/customer/agent NAMES the web UI renders, so
 * the admin never sees a bare id. All endpoints require admin role via
 * `AdminMiddleware`.
 *
 * @since 1.0.0
 */
import { Rpc, RpcGroup } from "@effect/rpc";
import { ResellerCommissionStatus } from "@fm/db/models/ResellerCommission";
import { RateTermsConfig, ResellerFeeType } from "@fm/db/models/ResellerRateTerms";
import { SubscriptionStatus } from "@fm/db/models/Subscription";
import { Schema } from "effect";
import { AdminMiddleware } from "../admin.tags.ts";
import { DatabaseError, Forbidden, RecordNotFoundError, Unauthorized, ValidationError } from "../errors.ts";
import { RateExceptionScope, ResellerCustomerInput, ResellerLedgerInput } from "./resellers.rpc.ts";

// ============================================================================
// Rate view schemas
// ============================================================================

/**
 * One flattened rate exception row for the admin rates screen: a single
 * (targetId, feeType) override with the resolved display name. A target
 * overriding both fee types yields TWO rows, one per fee type. `targetName` is
 * the customer name (customer scope) or `${agent name} · ${customer name}`
 * (agent scope); a dangling key that no longer resolves carries
 * `targetName === targetId`.
 * @since 1.0.0
 */
export const RateExceptionView = Schema.Struct({
  scope: RateExceptionScope,
  targetId: Schema.String,
  targetName: Schema.String,
  feeType: ResellerFeeType,
  floorAmount: Schema.Int,
  upsideShareBps: Schema.Int,
});
export type RateExceptionView = Schema.Schema.Type<typeof RateExceptionView>;

/**
 * The reseller's rate terms as the admin screen renders them: the raw stored
 * `config` (for the default-terms panel; null when none set) plus the flattened,
 * name-resolved exception rows.
 * @since 1.0.0
 */
export const RateTermsView = Schema.Struct({
  config: Schema.NullOr(RateTermsConfig),
  exceptions: Schema.Array(RateExceptionView),
});
export type RateTermsView = Schema.Schema.Type<typeof RateTermsView>;

// ============================================================================
// Ledger view schemas
// ============================================================================

/**
 * One name-resolved commission-ledger row for the earnings screen. `occurredAt`
 * is when the customer paid; `sourceCustomerName` is the paying customer's
 * account name; `agentName` is resolved via the accrual's subscription →
 * installation when resolvable, else null.
 * @since 1.0.0
 */
export const LedgerRowView = Schema.Struct({
  id: Schema.String,
  occurredAt: Schema.DateTimeUtc,
  periodMonth: Schema.String,
  sourceCustomerId: Schema.String,
  sourceCustomerName: Schema.String,
  agentName: Schema.NullOr(Schema.String),
  feeType: ResellerFeeType,
  grossAmount: Schema.Int,
  applicationFeeAmount: Schema.Int,
  commissionAmount: Schema.Int,
  status: ResellerCommissionStatus,
});
export type LedgerRowView = Schema.Schema.Type<typeof LedgerRowView>;

// ============================================================================
// Attributed / attributable customer schemas
// ============================================================================

/**
 * One attributed-customer row for the reseller customers screen: identity +
 * primary contact, the count of the customer's live agents, the customer's
 * current subscription status, and when it was attributed. `attributedAt` is
 * `customers.reseller_attributed_at` — the time the current attribution was
 * recorded, or null for a customer with no recorded attribution time.
 * @since 1.0.0
 */
export const AttributedCustomerView = Schema.Struct({
  id: Schema.String,
  name: Schema.String,
  contactName: Schema.NullOr(Schema.String),
  contactEmail: Schema.NullOr(Schema.String),
  agentCount: Schema.Int,
  subscriptionStatus: Schema.NullOr(SubscriptionStatus),
  attributedAt: Schema.NullOr(Schema.DateTimeUtc),
});
export type AttributedCustomerView = Schema.Schema.Type<typeof AttributedCustomerView>;

/**
 * Search a reseller's ATTRIBUTABLE customers by free text (customer name /
 * primary-contact name / email). `resellerCustomerId` scopes the `attributable`
 * flag on each result.
 * @since 1.0.0
 */
export const AttributableCustomerSearchInput = Schema.Struct({
  resellerCustomerId: Schema.String,
  query: Schema.String,
});
export type AttributableCustomerSearchInput = Schema.Schema.Type<typeof AttributableCustomerSearchInput>;

/**
 * One candidate row in the attribute-a-customer search: identity + primary
 * contact, the customer's live-agent count, `attributable` (true only when the
 * customer is unattributed — false when already attributed to any reseller),
 * and `attributedHere` (true when the customer is already attributed to the
 * reseller being viewed — used to distinguish "Already attributed" from
 * "Attributed elsewhere").
 * @since 1.0.0
 */
export const AttributableCustomer = Schema.Struct({
  id: Schema.String,
  name: Schema.String,
  contactName: Schema.NullOr(Schema.String),
  contactEmail: Schema.NullOr(Schema.String),
  agentCount: Schema.Int,
  attributable: Schema.Boolean,
  attributedHere: Schema.Boolean,
});
export type AttributableCustomer = Schema.Schema.Type<typeof AttributableCustomer>;

/**
 * Attach / detach a customer to/from a reseller.
 * @since 1.0.0
 */
export const AttributionInput = Schema.Struct({
  resellerCustomerId: Schema.String,
  customerId: Schema.String,
});
export type AttributionInput = Schema.Schema.Type<typeof AttributionInput>;

// ============================================================================
// RPC Group
// ============================================================================

/**
 * Admin Reseller VIEWS RPC group — the name-resolved read models + attribution
 * writes the rates / customers / earnings admin screens need. Every method
 * requires admin role via `AdminMiddleware`.
 *
 * @since 1.0.0
 */
export class AdminResellerViewsRpcs extends RpcGroup.make(
  Rpc.make("admin.resellers.rates.view", {
    payload: ResellerCustomerInput,
    success: RateTermsView,
    error: Schema.Union(Unauthorized, Forbidden, RecordNotFoundError, DatabaseError),
  }).middleware(AdminMiddleware),
  Rpc.make("admin.resellers.ledger.view", {
    payload: ResellerLedgerInput,
    success: Schema.Array(LedgerRowView),
    error: Schema.Union(Unauthorized, Forbidden, DatabaseError),
  }).middleware(AdminMiddleware),
  Rpc.make("admin.resellers.customers.view", {
    payload: ResellerCustomerInput,
    success: Schema.Array(AttributedCustomerView),
    error: Schema.Union(Unauthorized, Forbidden, DatabaseError),
  }).middleware(AdminMiddleware),
  Rpc.make("admin.resellers.customers.search", {
    payload: AttributableCustomerSearchInput,
    success: Schema.Array(AttributableCustomer),
    error: Schema.Union(Unauthorized, Forbidden, DatabaseError),
  }).middleware(AdminMiddleware),
  Rpc.make("admin.resellers.customers.attach", {
    payload: AttributionInput,
    success: Schema.Void,
    error: Schema.Union(Unauthorized, Forbidden, RecordNotFoundError, ValidationError, DatabaseError),
  }).middleware(AdminMiddleware),
  Rpc.make("admin.resellers.customers.detach", {
    payload: AttributionInput,
    success: Schema.Void,
    error: Schema.Union(Unauthorized, Forbidden, RecordNotFoundError, ValidationError, DatabaseError),
  }).middleware(AdminMiddleware),
) {}
